import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Pencil, Trash2, Loader2, MoreVertical, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, formatErr, rm } from "@/lib/api";

const joined = (iso) => new Date(iso).toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric", timeZone: "Asia/Kuala_Lumpur" });
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AdminHeader } from "@/components/platform/AdminHeader";
import { UserView, UserEdit, UserRemove } from "@/components/platform/UserDialogs";

const SortTh = ({ k, label, sort, setSort, className = "" }) => {
  const on = sort.key === k;
  const I = on ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  const click = () => setSort(on ? { key: k, dir: sort.dir === "asc" ? "desc" : "asc" } : { key: k, dir: "desc" });
  return (
    <th className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${className}`} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={click} data-testid={`users-sort-${k}`} className={`inline-flex items-center gap-1 uppercase tracking-wide hover:text-white ${on ? "text-emerald-300" : "text-slate-500"}`}>{label}<I className="h-3 w-3" /></button>
    </th>
  );
};
const Th = ({ children, className = "" }) => <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 ${className}`}>{children}</th>;
const Actions = ({ u, onView, onEdit, onRemove }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild><button type="button" title="Actions" data-testid={`user-actions-${u.id}`} className="grid h-8 w-8 place-items-center rounded-full text-slate-300 hover:bg-slate-800"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="border-slate-800 bg-[#0B1220] text-slate-200">
      <DropdownMenuItem onSelect={onView} data-testid={`user-view-${u.id}`}><Eye className="mr-2 h-4 w-4" />View</DropdownMenuItem>
      <DropdownMenuItem onSelect={onEdit} data-testid={`user-edit-${u.id}`}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>
      <DropdownMenuItem onSelect={onRemove} className="text-rose-300 focus:text-rose-200" data-testid={`user-remove-${u.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

function Row({ u, onView, onEdit, onRemove }) {
  return (
    <tr className="border-t border-slate-800" data-testid={`user-row-${u.id}`}>
      <td className="w-10 px-1 py-2.5 text-left"><Actions u={u} onView={onView} onEdit={onEdit} onRemove={onRemove} /></td>
      <td className="max-w-[200px] px-3 py-2.5 md:max-w-none"><p className="break-words font-semibold text-white">{u.name}</p><p className="break-all text-xs text-slate-500">{u.email}</p></td>
      <td className="hidden whitespace-nowrap px-3 py-2.5 text-slate-400 sm:table-cell" data-testid={`user-joined-${u.id}`}>{joined(u.created_at)}</td>
      <td className="hidden px-3 py-2.5 text-right text-slate-300 sm:table-cell">{u.orders}</td>
      <td className="hidden whitespace-nowrap px-3 py-2.5 text-right text-slate-300 sm:table-cell">{rm(u.spent_sen)}</td>
    </tr>
  );
}

export function UsersAdmin() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSortRaw] = useState({ key: "joined", dir: "desc" });
  const setSort = (s) => { setSortRaw(s); setPage(1); };
  const [d, setD] = useState(null);
  const [view, setView] = useState(null);
  const [, setSp] = useSearchParams();
  const openOrders = (u, order) => { setView(null); setSp({ s: "orders/customers", player: u.id, pname: u.name, ...(order ? { order } : {}) }); window.scrollTo(0, 0); };
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/asv/admin/users", { params: { q, page, size: 25, sort: sort.key, dir: sort.dir } }).then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))), [q, page, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const [del, setDel] = useState(null);
  const remove = () => api.delete(`/asv/admin/users/${del.id}`).then(() => { toast.success("User removed"); setDel(null); load(); }).catch((e) => toast.error(formatErr(e)));
  const pages = d ? Math.max(1, Math.ceil(d.total / d.size)) : 1;
  return (
    <div data-testid="users-admin">
      <AdminHeader title="Users" sub="Everyone who registered on the directory">
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name, email or phone" className="w-72" data-testid="users-search" />
      </AdminHeader>
      <div className="card-surface overflow-x-auto p-2">
        {!d ? <Loader2 className="m-6 h-6 w-6 animate-spin text-emerald-400" /> : <table className="w-full text-sm" data-testid="users-table">
          <thead><tr><Th className="w-10"><span className="sr-only">Actions</span></Th><Th>Name</Th><SortTh k="joined" label="Joined" sort={sort} setSort={setSort} className="hidden text-left sm:table-cell" />
            <SortTh k="orders" label="Orders" sort={sort} setSort={setSort} className="hidden text-right sm:table-cell" /><SortTh k="spent" label="Spent" sort={sort} setSort={setSort} className="hidden text-right sm:table-cell" /></tr></thead>
          <tbody>{d.items.map((u) => <Row key={u.id} u={u} onView={() => setView(u.id)} onEdit={() => setEdit(u)} onRemove={() => setDel(u)} />)}</tbody>
        </table>}
        {d && !d.items.length && <p className="p-6 text-center text-sm text-slate-500" data-testid="users-empty">No users found.</p>}
      </div>
      {d && <div className="mt-4 flex items-center justify-between text-sm text-slate-400">
        <span data-testid="users-total">{d.total} user{d.total === 1 ? "" : "s"}</span>
        <div className="flex items-center gap-2"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="users-prev">Prev</Button>
          <span data-testid="users-page">{page} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="users-next">Next</Button></div>
      </div>}
      <UserView id={view} onClose={() => setView(null)} onOrders={openOrders} onEdit={(u) => { setView(null); setEdit(u); }} />
      <UserRemove user={del} onClose={() => setDel(null)} onConfirm={remove} />
      <UserEdit user={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
